package net.frozenbit.games.soundgame.audio

import external.AudioBuffer
import external.AudioContext
import kotlinx.browser.document
import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import net.frozenbit.games.soundgame.utils.toJsArray
import org.w3c.dom.HTMLAudioElement
import org.w3c.dom.events.Event

class WebAudioPlayer: AudioPlayer {
    private val audioElement: HTMLAudioElement by lazy {
        val existingAudioElement = document.getElementById("music") as? HTMLAudioElement
        existingAudioElement ?: run {
            val newAudioElement = document.createElement("audio") as HTMLAudioElement
            newAudioElement.id = "music"
            newAudioElement.controls = false
            document.body?.appendChild(newAudioElement)
            newAudioElement
        }
    }

    override fun prepareMusic(path: String, loop: Boolean) {
        audioElement.src = path
        audioElement.load()
        audioElement.loop = loop
        audioElement.currentTime = 0.0
    }

    override fun setMusicVolume(volume: Double) {
        audioElement.volume = volume
    }

    override fun getMusicVolume(): Double {
        return audioElement.volume
    }

    override fun pauseMusic() {
        audioElement.pause()
    }

    override fun resumeMusic() {
        audioElement.play()
    }

    override fun seekMusic(position: Double) {
        audioElement.currentTime = position
    }

    override fun getMusicPosition(): Double {
        return audioElement.currentTime
    }

    override fun stopMusic() {
        audioElement.pause()
        audioElement.currentTime = 0.0
        audioElement.src = ""
    }

    private var nextHandlerId: Int = 1
    private fun generateEventHandlerHandle(): EventHandlerHandle {
        return EventHandlerHandle("handler_${nextHandlerId++}")
    }

    private val musicCompletionListeners = mutableMapOf<EventHandlerHandle, (Event) -> Unit>()

    override fun addMusicCompletionListener(listener: () -> Unit): EventHandlerHandle {
        val handle = generateEventHandlerHandle()
        val handler: (Event) -> Unit = { listener() }
        musicCompletionListeners[handle] = handler
        audioElement.addEventListener("ended", handler)
        return handle
    }

    override fun removeMusicCompletionListener(handle: EventHandlerHandle) {
        val handler = musicCompletionListeners.remove(handle)
        if (handler != null) {
            audioElement.removeEventListener("ended", handler)
        }
    }

    private val audioContext: AudioContext by lazy {
        AudioContext()
    }

    private val bufferMutex = Mutex()
    private var loadedAudioBuffers = 0
    private val audioBufferCache = mutableMapOf<AudioHandle, AudioBuffer>()

    override suspend fun prepare(buffer: ByteArray): AudioHandle {
        val audioBuffer = audioContext
            .decodeAudioData(buffer.toJsArray().buffer)
            .await()
        bufferMutex.withLock {
            val handle = AudioHandle("audio_${loadedAudioBuffers++}")
            audioBufferCache[handle] = audioBuffer
            return handle
        }
    }

    override suspend fun play(handle: AudioHandle, volume: Float) {
        val audioBuffer = audioBufferCache[handle]
        if (audioBuffer == null) {
            println("Attempted to play released audio! Handle: $handle")
            return
        }
        val source = audioContext.createBufferSource()
        source.buffer = audioBuffer
        source.connect(audioContext.destination)
        suspendCancellableCoroutine { continuation ->
            source.onended = {
                continuation.resumeWith(Result.success(Unit))
            }
            source.start()
            continuation.invokeOnCancellation { source.stop() }
        }
    }

    override suspend fun release(handle: AudioHandle) {
        bufferMutex.withLock {
            audioBufferCache.remove(handle)
        }
    }
}
